<script setup lang="ts">
/**
 * Primary component for '/:workspaceId/:projectId'
 *
 * Renders all agent details and should be composed of smaller components
 */
import { useStorage } from '@vueuse/core'
import { computed, onMounted, toRef, toRefs, watch } from 'vue'

import { useProjects } from '@/modules/Projects/useProjects'

import { getProject } from '@/backend/getProject'

import ConfirmationDialog from '@/uiKit/ConfirmationDialog.vue'

import { LDEvents, useABMetrics } from '@/modules/App/useABMetrics'
import { invariant } from '@/shared/utils/typeAssertions'
import ResizablePanels from '@/sharedComponents/ResizablePanels.vue'
import ErrorPage from '../App/ErrorPage.vue'
import CaseFileViewer from '../Cases/CaseFileViewer.vue'
import EntitySidebar from './EntitySidebar.vue'
import FilterBar from './Filters/FilterBar.vue'
import GroundingModal from './GroundingModal.vue'
import { useEntitySidebarNavigation } from './Navigation/useEntitySidebarNavigation'
import ProjectTable from './ProjectTable.vue'
import ProjectTableViewsTabstrip from './ProjectTableViewsTabstrip.vue'
import PropertySidebar from './PropertySidebar.vue'
import { useEntity } from './useEntity'
import { useEntitySidebarStore } from './useEntitySidebarStore'
import { useGroundingStore } from './useGroundingStore'
import { usePrevOrNextEntity } from './usePrevOrNextEntity'
import { serializeProperty, useProject } from './useProject'
import { provideProjectContext } from './useProjectContext'
import { useProjectSync } from './useProjectSync'
import { useProperty } from './useProperty'
import { usePropertySidebarIntegration } from './usePropertySidebarIntegration'
import { useSubProject } from './useSubProject'

const props = defineProps<{
  workspaceId: string
  projectId: string
  parentProjectId?: string
  parentEntityId?: string
  viewId?: string
  entityId?: string
}>()
const projectsStore = useProjects()
const project = computed(() => projectsStore.getProjectById(props.projectId))

const projectStore = useProject()
const subProjectStore = useSubProject()

const entityStore = useEntity()

const { loadingState } = useProjectSync(toRefs(props))
const { startTrackingTimeFor, trackElapsedTimeEvent } = useABMetrics()

const groundingStore = useGroundingStore()
watch(() => props.projectId, groundingStore.reset)

watch(
  () => projectStore.subProjectIds,
  (newIds) => {
    const oldIds = Object.keys(subProjectStore.projects)
    const added = newIds.filter((id) => !oldIds.includes(id))
    const removed = oldIds.filter((id) => !newIds.includes(id))

    added.forEach(async (id: string) => {
      const result = await getProject(props.workspaceId, id)
      if (result.ok) {
        if (result.data.parent_property === null) {
          throw Error('Subproject must have a parent property')
        }
        subProjectStore.setProject(id, {
          id: result.data.id,
          name: result.data.name,
          workspaceId: result.data.workspace_id,
          properties: result.data.properties.map(serializeProperty),
          parentPropertyId: result.data.parent_property.id,
          updatedAt: result.data.updated_at,
        })
      }
    })
    removed.forEach((id: string) => {
      subProjectStore.removeProject(id)
    })
  },
  { immediate: true },
)

const widthsFromStorage = useStorage<typeof projectStore.widths>(
  `col-widths-${props.projectId}`,
  {},
)

onMounted(() => {
  if (widthsFromStorage.value && Object.keys(widthsFromStorage.value).length > 0) {
    projectStore.widths = widthsFromStorage.value
  }
})

watch(projectStore.widths, () => {
  widthsFromStorage.value = projectStore.widths
})

const {
  deleteProperty,
  deletePropertyConfirmationOpen,
  deselectProperty,
  isPropertyBusy,
  reprocessColumn,
  reprocessColumnConfirmationOpen,
  saveProperty,
} = usePropertySidebarIntegration()

const propertyStore = useProperty()
const onCloseSidebar = () => {
  propertyStore.sidebarIsOpen = false
  deselectProperty()
}
const onConfirmDelete = async () => {
  await deleteProperty()
  propertyStore.sidebarIsOpen = false
}

watch(
  () => propertyStore.savedProperty,
  (property) => {
    if (property) {
      // AB_PROPERTY_EDITOR EXPERIMENT - Metrics
      startTrackingTimeFor(LDEvents.TIME_SPENT_ON_EDITING)
    }
  },
)

const entitySidebarStore = useEntitySidebarStore()
const { loadAndSetEntity } = usePrevOrNextEntity()

watch(
  () => [props.entityId, projectStore.projectLoaded] as const,
  async ([entityId, projectLoaded]) => {
    if (!entityId) {
      entitySidebarStore.reset()
      return
    }

    // there's an entity id, so sidebar should open,
    // bu we can't show the entity sidebar until
    // project data (mainly properties) is loaded to
    if (!projectLoaded) {
      return
    }

    if (entityId) {
      // Set the entity ID in both stores
      entitySidebarStore.setSelectedEntityId(entityId)

      // Load the entity data
      await loadAndSetEntity(entityId)
    }
  },
  { immediate: true },
)

const { closeSidebar } = useEntitySidebarNavigation()
const cleanupAndCloseSidebar = () => {
  entitySidebarStore.reset()
  groundingStore.reset()
  closeSidebar()
}

/**
 * This is the first component that is rendered for a given project,
 * so it's the appropriate place to provide the project context
 */
provideProjectContext(toRef(props, 'projectId'))

const onChangeSidebarField = (propertyId: string) => {
  const field = entityStore.fileFields.find((f) => f.propertyId === propertyId)
  invariant(field, 'No field found with the given property id')

  entitySidebarStore.setSelectedFileField(field)
}

const panels = computed(() => [
  { id: 'table', minWidthPx: 350, defaultPercentage: 50, show: true },
  {
    id: 'entity',
    minWidthPx: 720,
    maxWidthVw: 70,
    defaultPercentage: 50,
    show: entitySidebarStore.sidebarIsOpen,
  },
  {
    id: 'property',
    minWidthPx: 250,
    maxWidthVw: 30,
    defaultPercentage: 30,
    show: propertyStore.sidebarIsOpen,
  },
])

const onCloseFileViewer = () => {
  groundingStore.reset()
  entitySidebarStore.clearSelectedFileField()
}
</script>

<template>
  <div
    v-if="project || loadingState === 'loading'"
    v-bind="$attrs"
    class="flex h-full flex-1 flex-col"
  >
    <GroundingModal v-if="!entitySidebarStore.selectedFileField" />
    <ResizablePanels
      :panels="panels"
      storage-key="agent-view-panels"
      data-test="project-table-container"
    >
      <template #table>
        <ProjectTable :parent-entity-id="parentEntityId" />
        <!-- File viewer overlay (positioned to the left of sidebar) -->
        <div
          v-if="entitySidebarStore.selectedFileField"
          class="absolute right-0 top-0 z-10 mb-2.5 mr-px size-full min-w-[650px] max-w-[800px]"
        >
          <CaseFileViewer
            class="size-full rounded-corner-12 border border-border-subtle bg-surface-primary"
            :field="entitySidebarStore.selectedFileField.field"
            :available-file-fields="entityStore.fileFields"
            @close="onCloseFileViewer"
            @change:field="onChangeSidebarField"
          />
        </div>
      </template>
      <template #entity>
        <div
          v-if="entitySidebarStore.sidebarIsOpen"
          class="relative size-full"
        >
          <EntitySidebar
            :entity-id="entityId"
            :workspace-id="workspaceId"
            :project-id="projectId"
            @close="cleanupAndCloseSidebar"
          />
        </div>
      </template>
      <template #property>
        <PropertySidebar
          v-if="propertyStore.sidebarIsOpen"
          :workspace-id="workspaceId"
          :project-id="projectId"
          :disabled="isPropertyBusy"
          @close="onCloseSidebar"
          @delete="deletePropertyConfirmationOpen = true"
          @update="
            () => {
              trackElapsedTimeEvent(LDEvents.TIME_SPENT_ON_EDITING)
              saveProperty('sidebar')
            }
          "
          @reprocess="reprocessColumnConfirmationOpen = true"
        />
      </template>
    </ResizablePanels>

    <div class="flex flex-col">
      <FilterBar />
      <ProjectTableViewsTabstrip />
    </div>
  </div>
  <ErrorPage
    v-else
    title="Project not found"
    message="We could not find the page you're looking for."
  />

  <ConfirmationDialog
    id="delete-property-confirmation"
    :open="deletePropertyConfirmationOpen"
    title="Delete this property?"
    description="This property will be deleted immediately. You can't undo this action."
    @confirm="onConfirmDelete"
    @close="deletePropertyConfirmationOpen = false"
  />

  <ConfirmationDialog
    id="reprocess-property-confirmation"
    :open="reprocessColumnConfirmationOpen"
    title="Recompute all stale fields for this property?"
    description="This may take a while and become quite costly."
    variant="black"
    confirm-text="Confirm"
    @confirm="reprocessColumn"
    @close="reprocessColumnConfirmationOpen = false"
  />
</template>
